/* screens/workspace/canvas/CanvasViewForm.jsx — единый каркас inline-канвасов (S6).
   Содержит общий «скелет» канваса (шапка eyebrow+title+actions+×, прокручиваемое тело
   с --canvas-max-height, подвал), хук загрузки useCanvasLoad (loading/partial/error/ready
   через реальные api-загрузчики lib/api), и переиспользуемые блоки: CanvasSkeleton, CanvasErrorBanner,
   CanvasMetrics, CanvasSection, PriorityTag, RiskTag, TrendCell.
   Все 5 канвасов (TTK/Menu/ABC/Foodcost/Inventory) собираются из этих частей.
   Данные приходят ТОЛЬКО через спеку/пропсы (без импорта моков внутри блоков).
   ХЕНДОФФ → frontend/components/chat/CanvasViewForm.jsx. */

import React from 'react';
import { Icon } from '../../../components/ui/Icon.jsx';
import * as api from '../../../lib/api/index.js';
import { reportsReal } from '../../../lib/adapters/reports.js';
import { fmt } from '../../../lib/format.js';
import { RR } from '../../../lib/router.jsx';

  const { useState, useEffect, useRef, useCallback } = React;
  const { useNavigate } = RR;

  /* loader по типу канваса — боевой фасад (getTTKDetail/getMenuDetailReal/reportsReal.getReport).
     Все бросают err{status} через unwrap → useCanvasLoad.fail отрисует 403/404/ошибку. */
  function loaderFor(spec) {
    switch (spec.type) {
      case 'ttk': return () => api.getTTKDetail(spec.id);
      case 'menu': return () => api.getMenuDetailReal(spec.id);
      case 'abc':
      case 'foodcost':
      case 'inventory': return () => reportsReal.getReport(spec.id);
      default: return () => Promise.reject(new Error('Неизвестный тип канваса'));
    }
  }

  /* useCanvasLoad(spec) → { kind, data, partial, error, retry }
     spec: { type, id, progressive?, partial?, fail? }
     error: { status } — сохраняем HTTP-статус из реджекта загрузчика, чтобы канвас мог
     отрисовать ForbiddenState на 403 (без зацикленного «Повторить») и not-found на 404 (P1-31). */
  function useCanvasLoad(spec) {
    const [state, setState] = useState({ kind: 'loading', data: null, partial: null, error: null });
    const cancelRef = useRef(null);
    const tick = useRef(0);

    const fail = (err) => ({ kind: 'error', data: null, partial: null, error: { status: err && err.status, message: err && err.message } });

    const run = useCallback(() => {
      tick.current += 1;
      const myTick = tick.current;
      if (cancelRef.current) cancelRef.current();
      // chat-2: progressive-спека без id — канвас ещё собирается стримом (id придёт на ready,
      // тогда спека в metadata.canvas сменится на {type,id} → этот эффект перезапустится и
      // загрузит реальную сущность). До этого момента держим loading-скелет, БЕЗ запроса.
      if (spec.id == null) {
        setState({ kind: spec.partial ? 'partial' : 'loading', data: null, partial: spec.partial || null, error: null });
        cancelRef.current = null;
        return;
      }
      setState({ kind: 'loading', data: null, partial: null, error: null });
      const loadDone = loaderFor(spec);
      Promise.resolve().then(loadDone)
        .then((d) => { if (myTick === tick.current) setState({ kind: 'ready', data: d, partial: null, error: null }); })
        .catch((err) => { if (myTick === tick.current) setState(fail(err)); });
    }, [spec.type, spec.id, spec.progressive]);

    useEffect(() => { run(); return () => { if (cancelRef.current) cancelRef.current(); }; }, [run]);
    return { ...state, retry: run };
  }

  /* ── CanvasViewForm — каркас read-режима ── */
  function CanvasViewForm({ eyebrow, icon = 'sparkles', title, openHref, openLabel, onOpen, historyHref, onHistory, onClose, footer, panel, busy, children }) {
    const navigate = useNavigate();
    const go = (href, handler) => (e) => {
      e.preventDefault();
      if (handler) handler(); else if (href) navigate(href);
    };
    return (
      <section className={'canvas' + (panel ? ' canvas--panel' : '') + (busy ? ' is-busy' : '')} aria-label={title || eyebrow}>
        <header className="canvas__head">
          <span className="canvas__glyph" aria-hidden="true"><Icon name={icon} size={16} /></span>
          <div className="canvas__titles">
            <span className="canvas__eyebrow t-eyebrow">{eyebrow}</span>
            <h3 className="canvas__title" title={title}>{title}</h3>
          </div>
          <div className="canvas__head-actions">
            {(historyHref || onHistory) && (
              <a className="canvas__link" href={historyHref || '#'} onClick={go(historyHref, onHistory)}>
                История <Icon name="arrow-right" size={13} />
              </a>
            )}
            {(openHref || onOpen) && (
              <a className="canvas__link canvas__link--strong" href={openHref || '#'} onClick={go(openHref, onOpen)}>
                {openLabel || 'Открыть'} <Icon name="arrow-up-right" size={13} />
              </a>
            )}
            {onClose && (
              <button type="button" className="canvas__close" onClick={onClose} aria-label="Закрыть канвас">
                <Icon name="close" size={16} />
              </button>
            )}
          </div>
        </header>
        <div className="canvas__body">{children}</div>
        {footer && <footer className="canvas__foot">{footer}</footer>}
      </section>
    );
  }

  /* ── Состояния ── */
  function CanvasSkeleton({ note }) {
    return (
      <div className="canvas-skel" role="status" aria-live="polite">
        {note && <div className="canvas-skel__note"><Icon name="spinner" size={14} /> {note}</div>}
        <div className="canvas-skel__bar" style={{ width: '52%' }} />
        <div className="canvas-skel__row">
          <span className="canvas-skel__bar" style={{ width: '30%' }} />
          <span className="canvas-skel__bar" style={{ width: '30%' }} />
          <span className="canvas-skel__bar" style={{ width: '30%' }} />
        </div>
        <div className="canvas-skel__bar" style={{ width: '92%' }} />
        <div className="canvas-skel__bar" style={{ width: '86%' }} />
        <div className="canvas-skel__bar" style={{ width: '74%' }} />
      </div>
    );
  }

  function CanvasErrorBanner({ message = 'Не удалось загрузить данные', onRetry }) {
    return (
      <div className="canvas-error" role="alert">
        <Icon name="alert-circle" size={18} />
        <span className="grow">{message}</span>
        {onRetry && <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}><Icon name="refresh" size={14} /><span className="btn__label">Повторить</span></button>}
      </div>
    );
  }

  /* CanvasForbiddenBanner — 403 в канвасе: нет доступа, БЕЗ «Повторить» (ретрай зациклит). */
  function CanvasForbiddenBanner({ message = 'Нет доступа к этой сущности. Обратитесь к администратору пространства.' }) {
    return (
      <div className="canvas-error canvas-error--forbidden" role="alert">
        <Icon name="lock" size={18} />
        <span className="grow">{message}</span>
      </div>
    );
  }

  /* CanvasErrorState — диспетчер по error.status: 403 → ForbiddenBanner (без ретрая),
     404 → not-found (без ретрая), прочее → ErrorBanner с «Повторить» (P1-31). */
  function CanvasErrorState({ error, message = 'Не удалось загрузить данные', notFoundMessage = 'Сущность не найдена или была удалена.', onRetry }) {
    const status = error && error.status;
    if (status === 403) return <CanvasForbiddenBanner message={(error && error.message) || undefined} />;
    if (status === 404) return <CanvasErrorBanner message={notFoundMessage} />;
    return <CanvasErrorBanner message={message} onRetry={onRetry} />;
  }

  /* ── CanvasSection — секция канваса с заголовком ── */
  function CanvasSection({ title, hint, action, children, className }) {
    return (
      <section className={'canvas-sec' + (className ? ' ' + className : '')}>
        {(title || action) && (
          <div className="canvas-sec__head">
            <h4 className="canvas-sec__title t-eyebrow">{title}</h4>
            {hint && <span className="canvas-sec__hint t-caption text-mute">{hint}</span>}
            {action && <span className="canvas-sec__action">{action}</span>}
          </div>
        )}
        {children}
      </section>
    );
  }

  /* ── CanvasMetrics — строка метрик label/value (2×2 на узких) ── */
  function CanvasMetrics({ items = [] }) {
    return (
      <div className="canvas-metrics" style={{ '--cm-count': items.length }}>
        {items.map((m, i) => (
          <div className={'canvas-metric' + (m.tone ? ' canvas-metric--' + m.tone : '')} key={i}>
            <span className="canvas-metric__val num">{m.value}</span>
            <span className="canvas-metric__lbl t-caption">{m.label}</span>
          </div>
        ))}
      </div>
    );
  }

  /* приоритет ABC: P1/P2/P3/потенциал/норма → цвет+глиф+текст.
     Ключи в верхнем регистре + 'norm' — мок-канвасы; нижний регистр + 'normal' — backend-схема
     (reports/schemas.py ABCPosition.priority). Оба набора живут вместе (аддитивно). */
  const PRIORITY = {
    P1: { tone: 'bad', icon: 'alert-circle', label: 'P1' },
    P2: { tone: 'warn', icon: 'alert-triangle', label: 'P2' },
    P3: { tone: 'neutral', icon: 'minus', label: 'P3' },
    potential: { tone: 'info', icon: 'arrow-up-right', label: 'Потенциал' },
    norm: { tone: 'ok', icon: 'check', label: 'Норма' },
    p1: { tone: 'bad', icon: 'alert-circle', label: 'P1' },
    p2: { tone: 'warn', icon: 'alert-triangle', label: 'P2' },
    p3: { tone: 'neutral', icon: 'minus', label: 'P3' },
    normal: { tone: 'ok', icon: 'check', label: 'Норма' },
  };
  function PriorityTag({ priority }) {
    const p = PRIORITY[priority] || PRIORITY.P3;
    return <span className={'tagpill tagpill--' + p.tone}><Icon name={p.icon} size={12} />{p.label}</span>;
  }

  const RISK = { low: { tone: 'ok', label: 'низкий риск' }, medium: { tone: 'warn', label: 'средний риск' }, high: { tone: 'bad', label: 'высокий риск' } };
  function RiskTag({ risk }) {
    const r = RISK[risk] || RISK.medium;
    return <span className={'tagpill tagpill--' + r.tone}><span className="tagpill__dot" aria-hidden="true" />{r.label}</span>;
  }

  /* FC-статус ABC. ok/warn/critical — мок-канвасы; no_data/good/above_target/critical/urgent —
     backend-шкала (reports/schemas.py ABCPosition.fc_status). Оба набора аддитивно. */
  function FcStatusTag({ status }) {
    const map = {
      ok: { tone: 'ok', icon: 'check', label: 'в норме' },
      warn: { tone: 'warn', icon: 'alert-triangle', label: 'высокий' },
      critical: { tone: 'bad', icon: 'alert-circle', label: 'критично' },
      no_data: { tone: 'neutral', icon: 'minus', label: 'нет данных' },
      good: { tone: 'ok', icon: 'check', label: 'в норме' },
      above_target: { tone: 'warn', icon: 'alert-triangle', label: 'выше цели' },
      urgent: { tone: 'bad', icon: 'alert-circle', label: 'срочно' },
    };
    const s = map[status] || map.warn;
    return <span className={'tagpill tagpill--' + s.tone}><Icon name={s.icon} size={12} />{s.label}</span>;
  }

  /* TrendCell — направление + ОЦЕНКА по смыслу (фикс №53: цвет = хорошо/плохо, не наивно).
     goodDirection: 'down' (для недостач/себест. снижение = хорошо). Стрелка вниз — повёрнутый
     arrow-up-right (в реестре нет отдельного arrow-down). */
  function TrendCell({ deltaRub, goodDirection = 'down', label }) {
    if (deltaRub == null || deltaRub === 0) return <span className="trend trend--flat"><Icon name="arrow-right" size={13} /> 0 ₽</span>;
    const dir = deltaRub > 0 ? 'up' : 'down';
    const good = dir === goodDirection;
    return (
      <span className={'trend trend--' + (good ? 'good' : 'bad')}>
        <span className={'trend__arrow trend__arrow--' + dir}><Icon name="arrow-up-right" size={13} /></span>
        <span className="num">{fmt.money(deltaRub, { sign: true })}</span>
        {label && <span className="trend__lbl">{label}</span>}
      </span>
    );
  }

export { useCanvasLoad };
export { CanvasViewForm };
export { CanvasSkeleton };
export { CanvasErrorBanner };
export { CanvasForbiddenBanner };
export { CanvasErrorState };
export { CanvasSection };
export { CanvasMetrics };
export { PriorityTag };
export { RiskTag };
export { FcStatusTag };
export { TrendCell };
